import LMSDispatcher from "../LMSDispatcher";

import { IApi, Api } from '../../utilities/Api';
import { ApiRequest } from '../../utilities/ApiRequest';
import * as ACTIONCONTANTS from './ActionConstants';
import GradeViewData, { IGradeViewData } from "../../models/grade/GradeViewData";
import MeasuredPropertyData, { IMeasuredPropertyData } from "../../models/shared/IMeasuredPropertyData";
import { defaultTestplanBasicData } from "../../models/testplan/TestplanBasicData";
import { IGradeFormData } from "../../models/grade/GradeFormData";
import { IPropertySpecification } from "../../models/shared/PropertySpecification";
import { ICalculatedPropertyData } from '../../models/shared/ICalculatedPropertyData';
import CalculatedPropertyData from '../../models/shared/ICalculatedPropertyData';
import ISampleGradeProductFormData from "../../models/shared/SampleGradeProductFormData";
import KeyedInPropertyData, { IKeyedInPropertyData } from "../../models/shared/IKeyedInPropertyData";
import PropertyViewData, { IPropertyViewData } from "../../models/property/PropertyViewData";
import * as CONSTANTS from "../../utilities/Constants";
import i18next from "i18next";
import { v4 as uuidv4 } from "uuid";

class GradeActions {
    private readonly api: IApi;

    constructor() {
        this.api = new Api(ACTIONCONTANTS.SAMPLE_ENDPOINT);
    }

    async getById(id: string) {
        try {
            const request = new ApiRequest("/Grade/" + id, "", null, i18next.t("Grade not found."));
            const result = await this.api.get(request);
            const payload = this.convertResponseData(result.data);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_GET_BY_ID,
                payload: {
                    gradeData: payload
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async getAll(status: boolean, page: number, pageSize: number, sort: string, order: string, filter: string) {
        try {
            const request = new ApiRequest(`/Grade/${status}/${page}/${pageSize}/${sort}/${order}/${filter}`, "", null, i18next.t("Internal server error!"));
            const result = await this.api.get(request);
            const payload = this.convertResponseListData(result.data);
            
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_GET_ALL,
                payload: {
                    gradeDataList: payload
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async post(data: any): Promise<void> {
        try {
            const payload: any = this.preparePostPayload(data);

            const request = new ApiRequest("/Grade", uuidv4(), payload, i18next.t("Grade created."));
            const result = await this.api.post(request);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_SUBMIT,
                payload: {
                    gradeId: result.data,
                    message: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async put(data: any): Promise<void> {
        try {
            const payload: any = this.preparePutPayload(data);

            const request = new ApiRequest("/Grade", data.requestId, payload, i18next.t("Grade updated."));
            const result = await this.api.put(request);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_SUBMIT,
                payload: {
                    gradeId: payload.id,
                    message: result.message
                }
            });

        } catch (error) {
            throw error;
        }
    }

    async deleteMultiple(ids: any[], requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/Grade", requestId, ids, i18next.t("Grade deleted"));
            const result = await this.api.deleteAll(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_DELETE,
                payload: {
                    reload: true,
                    message: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async enable(id: string, requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/grade/" + id + "/enable", requestId, undefined, i18next.t("Grade enabled"));
            const result = await this.api.patch(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_ENABLE,
                payload: {
                    reload: true,
                    messages: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async disable(id: string, requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/grade/" + id + "/disable", requestId, undefined, i18next.t("Grade disabled"));
            const result = await this.api.patch(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_DISABLE,
                payload: {
                    reload: true,
                    messages: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async createGradeSample(data: ISampleGradeProductFormData): Promise<void> {
        try {
            const payload: any = this.prepareGradeProductPostPayload(data);
            const request = new ApiRequest("/sampleExtension/addgrade", data.requestId, payload, i18next.t("Grade created."));
            const result = await this.api.post(request);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.GRADE_SUBMIT,
                payload: {
                    gradeId: result.data,
                    message: result.message
                }
            });

        } catch (error) {
            throw error;
        }
    }


    private prepareGradeProductPostPayload(data: ISampleGradeProductFormData): any {
        return {
            sampleId: data.sampleId,
            name: data.name,
            codeId: data.codeId
        };
    }


    private preparePostPayload(data: any): any {
        const measuredProperties: any[] = [];
        const keyedInProperties: any[] = [];
        const calculatedProperties: any[] = [];

        data.properties.forEach((item: any) => {
            if (item.isMeasured) {
                measuredProperties.push({
                    instrumentId: item.instrumentId,
                    propertyId: item.propertyId,
                    directionId: item.direction.id,
                    target: item.target,
                    sigma: item.sigma.includes("%") ? (parseFloat(item.target) * parseFloat(item.sigma) / 100).toString()  : item.sigma,
                    sigmaPercent: item.sigmaPercent ? item.sigmaPercent : false,
                    gain: item.gain ? item.gain : 1,
                                                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    order: item.order
                })
            } else if (item.isCalculated) {
                calculatedProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma.includes("%") ? (parseFloat(item.target) * parseFloat(item.sigma) / 100).toString()  : item.sigma,
                    sigmaPercent: item.sigmaPercent ? item.sigmaPercent : false,
                    gain: item.gain ? item.gain : 1,
                                                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    formulaExecutionId: "6f8d1410-5f3c-41fc-b619-6bbdc78ffaae",
                    sourceMappings: item.sourceMappings,
                    order: item.order
                });
            } else {
                keyedInProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma.includes("%") ? (parseFloat(item.target) * parseFloat(item.sigma) / 100).toString()  : item.sigma,
                    sigmaPercent: item.sigmaPercent ? item.sigmaPercent : false,
                    gain: item.gain ? item.gain : 1,
                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    order: item.order
                });
            }
        });
        if(data.gradeName){
            return {
                code: data.gradeCode,
                description: "",
                testPlanId: data.id,
                measuredProperties,
                keyedInProperties,
                calculatedProperties,
                name: data.gradeName,
            };
        } else{
            return {
                code: data.code,
                description: data.description,
                testPlanId: data.testplan.id,
                measuredProperties,
                keyedInProperties,
                calculatedProperties,
                name: data.name,
            };
        }
        
    }

    private preparePutPayload(data: any): any {
        const measuredProperties: any[] = [];
        const keyedInProperties: any[] = [];
        const calculatedProperties: any[] = [];

        data.properties.forEach((item: any) => {
            if (item.isMeasured) {
                measuredProperties.push({
                    instrumentId: item.instrumentId,
                    propertyId: item.propertyId,
                    directionId: item.direction.id,
                    target: item.target,
                    sigma: item.sigma,
                    gain: item.gain ? item.gain : 1,
                                                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    order: item.order
                })
            } else if (item.isCalculated) {
                calculatedProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma,
                    gain: item.gain ? item.gain : 1,
                                                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    formulaExecutionId: "6f8d1410-5f3c-41fc-b619-6bbdc78ffaae",
                    sourceMappings: item.sourceMappings,
                    order: item.order
                });
            } else {
                keyedInProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma,
                    gain: item.gain ? item.gain : 1,
                                                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    order: item.order
                });
            }
        });

        if(data.gradeName && data.gradeCode){
            return {
                id: data.gradeId,
                name: data.gradeName,
                code: data.gradeCode,
                description: "",
                testPlanId: data.id,
                measuredProperties,
                keyedInProperties,
                calculatedProperties
            };
        } else{
            return {
                id: data.id,
                name: data.name,
                code: data.code,
                description: data.description,
                testPlanId: data.testplan.id,
                measuredProperties,
                keyedInProperties,
                calculatedProperties
            };
        }
    }

    private convertResponseData(data: any): IGradeViewData {
        const measuredProperties: IMeasuredPropertyData[] = [];
        const keyedInProperties: IKeyedInPropertyData[] = [];
        const calculatedProperties: ICalculatedPropertyData[] = [];
        let propertyDetails: IPropertyViewData;

        data.measuredProperties.forEach((element: any) => {
            const direction = (element.direction.name != CONSTANTS.ND) ? " ( " + element.direction.name + ")" : "";
            const propertyNameDirection = element.property.name + direction;
            propertyDetails = new PropertyViewData(element.property.id, element.property.name, element.property.description,
                element.property.customId, element.property.type, element.property.typeId, element.property.unit,
                element.property.decimal, propertyNameDirection)
            measuredProperties.push(new MeasuredPropertyData(element.instrument,
                propertyDetails,
                element.target === null ? "" : element.target,
                element.sigma === null ? "" : element.sigma, element.gain, element.offset,
                element.minValue === null || element.minValue === 0 ? "" : element.minValue, element.maxValue === null || element.maxValue === 0 ? "" : element.maxValue,
                element.alarmLow1 === null ? "" : element.alarmLow1, element.alarmHigh1 === null ? "" : element.alarmHigh1,
                element.alarmLow2 === null ? "" : element.alarmLow2, element.alarmHigh2 === null ? "" : element.alarmHigh2,
                element.rejectLow === null ? "" : element.rejectLow, element.rejectHigh === null ? "" : element.rejectHigh,
                element.direction, element.order, element.sigmaPercent  ? element.sigmaPercent : false));
        });


        data.keyedInProperties.forEach((element: any) => {
            const propertyNameDirection = element.property.name;
            propertyDetails = new PropertyViewData(element.property.id, element.property.name, element.property.description,
                element.property.customId, element.property.type, element.property.typeId, element.property.unit,
                element.property.decimal, propertyNameDirection)
            keyedInProperties.push(new KeyedInPropertyData(propertyDetails,
                element.target === null ? "" : element.target,
                element.sigma === null ? "" : element.sigma, element.gain, element.offset,
                element.minValue === null || element.minValue === 0 ? "" : element.minValue, element.maxValue === null || element.maxValue === 0 ? "" : element.maxValue,
                element.alarmLow1 === null ? "" : element.alarmLow1, element.alarmHigh1 === null ? "" : element.alarmHigh1,
                element.alarmLow2 === null ? "" : element.alarmLow2, element.alarmHigh2 === null ? "" : element.alarmHigh2,
                element.rejectLow === null ? "" : element.rejectLow, element.rejectHigh === null ? "" : element.rejectHigh, element.order , element.sigmaPercent  ? element.sigmaPercent : false));
        });

        if (data.calculatedProperties.length > 0) {
            data.calculatedProperties.forEach((element: any) => {
                const propertyNameDirection = element.property.name;
                propertyDetails = new PropertyViewData(element.property.id, element.property.name, element.property.description,
                    element.property.customId, element.property.type, element.property.typeId, element.property.unit,
                    element.property.decimal, propertyNameDirection)
                calculatedProperties.push(new CalculatedPropertyData(propertyDetails,
                    element.target === null ? "" : element.target,
                    element.sigma === null ? "" : element.sigma, element.gain, element.offset,
                    element.minValue === null || element.minValue === 0 ? "" : element.minValue, element.maxValue === null || element.maxValue === 0 ? "" : element.maxValue,
                    element.alarmLow1 === null ? "" : element.alarmLow1, element.alarmHigh1 === null ? "" : element.alarmHigh1,
                    element.alarmLow2 === null ? "" : element.alarmLow2, element.alarmHigh2 === null ? "" : element.alarmHigh2,
                    element.rejectLow === null ? "" : element.rejectLow,
                    element.rejectHigh === null ? "" : element.rejectHigh,
                    element.sourceMappings, element.formulaMappings, element.order , element.sigmaPercent  ? element.sigmaPercent : false))
            });
        }

        return new GradeViewData(data.id, data.name, data.description, data.code, data.isEnabled, data.testPlan, measuredProperties,
            keyedInProperties, calculatedProperties);
    }

    private convertResponseListData(data: any) {
        const result: IGradeViewData[] = [];
        if (data.contents) {
            data.contents.forEach((element: any) => {
                result.push(new GradeViewData(element.id, element.name, element.description, element.code, element.isEnabled, element.testPlanDetail, [], [], [], element.testPlanDetail.name ,element.createdOn));
            });
        }
        else {
            data.forEach((element: any) => {
                result.push(new GradeViewData(element.id, element.name, element.description, element.code, element.isEnabled, element.testPlanDetail, [], [], [], element.testPlanDetail.name ,element.createdOn));
            });
        }
        const tempResult = {
            result : data.contents ? result : result,
            currentPage: data.contents ? data.currentPage : 0,
            totalPages: data.contents ?  data.totalPages : 0,
            totalRecords: data.contents ?  data.totalRecords : 0
        }
        return tempResult;
    }
}

const gradeActions = new GradeActions();
export default gradeActions;